import type { ActionId } from 'owa-mail-list-item-animation-store';
import type { TableView } from 'owa-mail-list-store';
import { getShouldAnimate } from './getShouldAnimate';
import {
    getAreRowKeysInExistingAnimation,
    addPendingAnimation,
    mailListItemAnimationStore,
} from 'owa-mail-list-item-animation-store';
import {
    getSelectedTableView,
    isItemPartOperation,
    MailRowDataPropertyGetter,
} from 'owa-mail-list-store';
import { selectNewItemUponRowRemoval } from 'owa-mail-actions/lib/mailListSelectionActions';
import shouldFirstRowBeSelectedOnLoad from 'owa-mail-list-store/lib/selectors/shouldFirstRowBeSelectedOnLoad';
import type { ActionSource } from 'owa-mail-store';
import { resetFocus } from 'owa-mail-focus-manager';
import onActionOrAnimationCompletedMutator from '../actions/onActionOrAnimationCompleted';

export function startAnimationOrHandleActionAsNeeded(
    actionId: ActionId,
    rowKeys: string[],
    actionSource: ActionSource,
    isActionTakenFromDialog: boolean,
    handleAnimation?: () => void,
    isReadingPane?: boolean
): void {
    if (!getAreRowKeysInExistingAnimation(rowKeys)) {
        const { activeAnimations, pendingAnimations, completedAnimations } =
            mailListItemAnimationStore();
        const ongoingAnimationsCount =
            activeAnimations.length + pendingAnimations.length + completedAnimations.length;
        let shouldNotAutoSelect = true;
        // We don't want to animate if the user is swiping the row because the swipe behavior already animates the row.
        if (
            !isReadingPane &&
            getShouldAnimate() &&
            ongoingAnimationsCount < 3 &&
            actionSource !== 'Swipe'
        ) {
            const tableView = getSelectedTableView();
            // Select the next row immediately so that we don't have to wait for the animation to complete.
            // However, we should not auto-select when:
            // 1) Is item part operation because it is a less common scenario for users to want to do rapid-fire-triage
            // 2) It's an add row animation because we don't want to change selection when a new row is being inserted into the ML
            // 3) The setting selected is "let me select which message to read first" + no selectedRowKeys in tableView
            // 4) Any one of the rows being removed isn't in the tableView's selectedRowKeys (I'm just checking the 1st row in rowKeys)
            shouldNotAutoSelect =
                isItemPartOperation() ||
                actionId === 'AddRowInitial' ||
                (!shouldFirstRowBeSelectedOnLoad(tableView) &&
                    tableView?.selectedRowKeys.size === 0) ||
                !tableView?.selectedRowKeys.has(rowKeys[0]);
            if (!shouldNotAutoSelect) {
                trySelectNextOrPreviousRow(rowKeys, tableView);
            }
            addPendingAnimation(rowKeys, actionId /*actionId*/, handleAnimation);
        } else {
            handleAnimation?.();
        }

        // If the action source is 'Hover' or if the action was taken from a dialog, we want to reset the focus to the mail list after the action is completed and we want to
        // clear the last auto focused row key so that we can force the previously focused mail list item to regain focus because focus
        // is on the mail list item that was just removed from the list.
        if ((actionSource === 'Hover' && shouldNotAutoSelect) || isActionTakenFromDialog) {
            resetFocus('hoverOrKeyboardTriageAction');
            setTimeout(() => {
                const selectedTableView = getSelectedTableView();
                if (selectedTableView) {
                    onActionOrAnimationCompletedMutator(selectedTableView);
                }
            }, 0);
        }
    }
    //no-op if any of the rowKeys are in an ongoing animation
}

function trySelectNextOrPreviousRow(rowKeys: string[], tableView: TableView | undefined) {
    const keyOfLastRowSelected = rowKeys[rowKeys.length - 1]; // This is the last row that the user selected, not necessarily the row with the highest index.

    if (keyOfLastRowSelected && tableView) {
        const indexOfLastRowSelected = tableView.rowKeys.indexOf(keyOfLastRowSelected);

        const lastSelectedRowWasPinned = MailRowDataPropertyGetter.getIsPinned(
            keyOfLastRowSelected,
            tableView
        );

        selectNewItemUponRowRemoval(
            tableView,
            indexOfLastRowSelected,
            lastSelectedRowWasPinned,
            true /*isRowNotRemovedYet*/,
            rowKeys
        );
    }
}
